import { useMemo, useState, useEffect } from "react";
import { ChainOfCustody } from "./Modals/ChainOfCustody";
import { useTranslation } from "react-i18next";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import Icon from "../../../../Icon/Icon";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { Separator, UploadedFile } from "../components/InvestigationComponents";
import dayjs from "dayjs";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { InvestigationButton } from "../components/InvestigationButton";
import _ from "lodash";
import { SingleChainOfCustody } from "./SingleChainOfCustody";
import { useConstants } from "../Main/useConstants";
import { useSelector } from "react-redux";
import { catalogByPart } from "../../../../../apis";

export const SingleEvidence = ({
  idx,
  evidence,
  evidences,
  setSelectedEvidence,
  setIsEditing,
  handleSaveEvidenceDetails,
  advanced,
  justView,
  openConfirmDeleteModal,
  closeModalConfirm,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const {
    enums,
    evidences: { kindOpts, fundamentaOpts, kindMoveCustodyOpts },
  } = useConstants();
  const jaguarState = useSelector((state) => state.jaguar);

  const [selectedChainOfCustody, setSelectedChainOfCustody] = useState(null);
  const [showChainOfCustody, setShowChainOfCustody] = useState(false);
  const [typeOpts, setTypeOpts] = useState([]);

  // Cargar catálogo de tipos de evidencia
  useEffect(() => {
    const loadEvidenceCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: true,
          catalogue: "evidence",
          path: "/",
        });

        if (resp?.data && Array.isArray(resp.data)) {
          const formattedOptions = resp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setTypeOpts(formattedOptions);
        }
      } catch (error) {
        console.error("Error loading evidence catalog:", error);
        setTypeOpts([]);
      }
    };

    loadEvidenceCatalog();
  }, []);

  const createdAt = useMemo(() => {
    const fecha = dayjs(evidence.information?.created_at).format("DD/MMM/YYYY");
    if (fecha == "Invalid Date") return t("Evidences.undefined");
    return fecha.toLocaleUpperCase();
  }, [evidence.information?.created_at]);

  const deadline = useMemo(() => {
    const fecha = dayjs(evidence.information?.deadline).format("DD/MMM/YYYY");
    if (fecha == "Invalid Date") return t("Evidences.undefined");
    return fecha.toLocaleUpperCase();
  }, [evidence.information?.deadline]);

  const handleEditEvidence = () => {
    setSelectedEvidence(idx);
    setIsEditing(true);
  };

  const openModalDeleteEvidence = () => {
    openConfirmDeleteModal({
      description: t("Evidences.only_delete_evidence"),
      onConfirm: handleDeleteEvidence,
    });
  };

  const handleDeleteEvidence = async () => {
    const newEvidences = _.cloneDeep(evidences);
    const currentEvidence = newEvidences[idx];
    currentEvidence.files = [];
    try {
      await handleSaveEvidenceDetails(newEvidences);
      closeModalConfirm();
    } catch (error) {
      console.log(error);
    }
  };

  const actions = [
    {
      name: t("speeddial.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.primary.main} />,
      onClick: handleEditEvidence,
    },
    ...(evidence.kind !== "fisic" &&
    evidence.kind === "digital" &&
    evidence.files.length > 0
      ? [
          {
            name: t("speeddial.delete"),
            icon: <Icon name="trash_can" color={theme.palette.error.main} />,
            onClick: openModalDeleteEvidence,
          },
        ]
      : []),
  ];

  return (
    <>
      <ChainOfCustody // Modal
        show={showChainOfCustody}
        onHide={() => setShowChainOfCustody(false)}
        selectedChainOfCustody={selectedChainOfCustody}
        setSelectedChainOfCustody={setSelectedChainOfCustody}
        evidences={evidences}
        idxEvidence={idx} // index de la evidencia
        idxFile={0} // index de el archivo
        handleSaveEvidenceDetails={handleSaveEvidenceDetails}
        existsChainOfCustody={
          Array.isArray(evidence.cocs) && evidence.cocs.length > 0
        }
      />
      <EGAccordionSingle
        title={
          evidence.upload_by?.from === enums.typeUser.COMPLAINANT
            ? `${t("Evidences.evidence_provided_by")}: ${t("type_users.COMPLAINANT")} (${
                typeOpts.find((opt) => opt.value === evidence.type)?.label ||
                t("Evidences.undefined")
              })`
            : ` ${t("Evidences.evidence_provided_by")}:
            ${
              _.get(
                jaguarState,
                enums.typeUserRoutes[evidence.upload_by?.from]
              )?.formatted.find((opt) => opt.value === evidence.upload_by?.id)
                ?.label || t("Evidences.undefined")
            } (${
                typeOpts.find((opt) => opt.value === evidence.type)?.label ||
                t("Evidences.undefined")
              })`
        }
        bgColor={theme.palette.blueGray500.main}
        color={theme.palette.oxfordBlue.main}
        actions={!justView ? actions : undefined}
      >
        <Grid2 container spacing={2}>
          {evidence.upload_by_details && (
            <Grid2 size={{ xs: 12, md: 6 }} my={2}>
              <InvestigationBlockInfo
                label={t("Evidences.add_details_how_obtained")}
                text={evidence.upload_by_details}
              />
            </Grid2>
          )}
          {evidence.type_details && (
            <Grid2 size={{ xs: 12, md: 6 }} my={2}>
              <InvestigationBlockInfo
                label={t("Evidences.add_details_of_evidence")}
                text={evidence.type_details}
              />
            </Grid2>
          )}
        </Grid2>

        <Separator
          text={t(
            `${t("Evidences.evidence")}: ${
              kindOpts.find((opt) => opt.value === evidence.kind)?.label
            }`
          )}
        />

        <Grid2 container spacing={2} mt={2}>
          <Grid2
            size={{ xs: 12, md: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            <Icon name="timer2_bold" color={"#F4996C"} />
            <Typography
              fontSize={14}
              fontWeight={600}
              component="span"
              color={theme.palette.secondary.main}
            >
              {t("Evidences.reception_date")}: &nbsp;
              <Typography
                component="span"
                fontWeight={700}
                fontSize={16}
                color={"#F4996C"}
              >
                {createdAt}
              </Typography>
            </Typography>
          </Grid2>

          <Grid2
            size={{ xs: 12, md: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            {evidence.kind === "digital" && (
              <>
                <Icon name="timer2_bold" color={theme.palette.red.main} />
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  color={theme.palette.secondary.main}
                >
                  {t("Evidences.the_evidence_expires")}: &nbsp;
                  <Typography
                    component="span"
                    fontWeight={700}
                    fontSize={16}
                    color={theme.palette.red.main}
                  >
                    {deadline}
                  </Typography>
                </Typography>
              </>
            )}
          </Grid2>

          <Grid2
            size={{ xs: 12 }}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            gap={1}
          >
            {evidence.files.length > 0 &&
              evidence.files.map((file, idx) => (
                <UploadedFile
                  key={idx}
                  text={file.name}
                  downloadUrl={file.file_url}
                  sx={{ flex: "0 1 258px" }}
                />
              ))}
          </Grid2>

          {evidence.description && (
            <Grid2 size={12} display="flex" alignItems="center" gap={1}>
              <InvestigationBlockInfo
                label={
                  evidence.type === "fisic"
                    ? t("Evidences.details_of_the_archive")
                    : t("Evidences.details_of_the_evidence")
                }
                size={12}
                text={
                  <Box pb={0.2}>
                    <RichTextEditor
                      value={evidence.description}
                      justView={true}
                    />
                  </Box>
                }
              />
            </Grid2>
          )}

          {evidence.information?.position && (
            <Grid2 size={12} display="flex" alignItems="center" gap={1}>
              <InvestigationBlockInfo
                label={t("Evidences.position")}
                size={12}
                text={
                  <Box pb={0.2}>
                    <RichTextEditor
                      value={evidence.information?.position}
                      justView={true}
                    />
                  </Box>
                }
              />
            </Grid2>
          )}
        </Grid2>

        <Grid2 container spacing={2}>
          {evidence.information.compromised && (
            <Grid2 size={{ xs: 12, md: 4 }} mt={2}>
              <InvestigationItemContainer color="#EE7A1E" sx={{ p: 2 }}>
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  mb={evidence.information.compromised_details ? 1 : 0}
                  color={theme.palette.secondary.main}
                >
                  {t("Evidences.compromised_evidence")}
                </Typography>
                {evidence.information.compromised_details && (
                  <>
                    <hr style={{ marginTop: 0, marginBottom: 10 }} />
                    <InvestigationBlockInfo
                      label={t("Evidences.details_of_compromised_evidence")}
                      text={evidence.information.compromised_details}
                      underline={false}
                      size={12}
                    />
                  </>
                )}
              </InvestigationItemContainer>
            </Grid2>
          )}

          {evidence.information.measures && (
            <Grid2 size={{ xs: 12, md: 4 }} mt={2}>
              <InvestigationItemContainer color="#B694FF" sx={{ p: 2 }}>
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  mb={evidence.information.measures_details ? 1 : 0}
                  color={theme.palette.secondary.main}
                >
                  {t("Evidences.actions_of_preservation_of_evidence")}
                </Typography>
                {evidence.information.measures_details && (
                  <>
                    <hr style={{ marginTop: 0, marginBottom: 10 }} />
                    <InvestigationBlockInfo
                      label={t("Evidences.details_of_actions_to_preserve_the_evidence")}
                      text={evidence.information.measures_details}
                      underline={false}
                      size={12}
                    />
                  </>
                )}
              </InvestigationItemContainer>
            </Grid2>
          )}

          {evidence.information.fundamentals && (
            <Grid2 size={{ xs: 12, md: 4 }} mt={2}>
              <InvestigationItemContainer
                color={theme.palette.yellow.main}
                sx={{
                  p: 2,
                }}
              >
                <Typography
                  fontSize={14}
                  mb={evidence.information.fundamentals_details ? 1 : 0}
                  color={theme.palette.secondary.main}
                >
                  {t("Evidences.fundamenta_the_report")}: &nbsp;
                  <Typography
                    component="span"
                    fontSize={14}
                    fontWeight={700}
                    color={theme.palette.yellow.main}
                  >
                    {
                      fundamentaOpts.find(
                        (opt) =>
                          opt.value === evidence.information?.fundamentals
                      )?.label
                    }
                  </Typography>
                </Typography>
                {evidence.information.fundamentals_details && (
                  <>
                    <hr style={{ marginTop: 0, marginBottom: 10 }} />
                    <InvestigationBlockInfo
                      label={t("Evidences.details")}
                      text={evidence.information.fundamentals_details}
                      underline={false}
                      size={12}
                    />
                  </>
                )}
              </InvestigationItemContainer>
            </Grid2>
          )}
        </Grid2>

        {advanced &&
          Array.isArray(evidence.cocs) &&
          evidence.cocs.length > 0 && (
            <>
              <Separator text={t("Evidences.chain_of_custody")} sx={{ mt: 2 }} />
              <Stack spacing={2} mt={2}>
                {evidence.cocs.map((coc, idxCoc) => (
                  <SingleChainOfCustody
                    key={idxCoc}
                    idxCoc={idxCoc}
                    coc={coc}
                    selectedChainOfCustody={selectedChainOfCustody}
                    setSelectedChainOfCustody={setSelectedChainOfCustody}
                    setShowChainOfCustody={setShowChainOfCustody}
                    idxEvidence={idx}
                    evidences={evidences}
                    handleSaveEvidenceDetails={handleSaveEvidenceDetails}
                    kindMoveCustodyOpts={kindMoveCustodyOpts}
                    justView={justView}
                  />
                ))}
              </Stack>
            </>
          )}

        {advanced && evidence.kind === "fisic" && !justView ? (
          <Box display="flex" justifyContent="flex-end" spacing={1} mt={1.5}>
            <InvestigationButton
              label={
                Array.isArray(evidence.cocs) && evidence.cocs.length > 0
                  ? t("Evidences.add_movement")
                  : t("Evidences.chain_of_custody")
              }
              onClick={() => setShowChainOfCustody(true)}
            />
          </Box>
        ) : (
          evidence.kind === "digital" &&
          evidence.files.length > 0 &&
          !justView && (
            <Box display="flex" justifyContent="flex-end" spacing={1} mt={1.5}>
              <InvestigationButton
                label={t("Evidences.delete_evidence")}
                variant="danger"
                onClick={openModalDeleteEvidence}
              />
            </Box>
          )
        )}
      </EGAccordionSingle>
    </>
  );
};
